import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useHistory, useLocation} from 'react-router-dom';
import {Accordion, Button, Divider, Header, Icon, Label} from "semantic-ui-react";
import {customerReport, fetchCustomerReport} from "../../../../style/page_with_sidebar";
import {ConfigContext} from "../../../context/ConfigContext";
import {CustomerReportApiGatewayStatus} from "./sections/CustomerReportApiGatewayStatus";
import {CustomerReportSftpStatus} from "./sections/CustomerReportSftpStatus";
import {CustomerReportCustomerFileHealthStatus} from "./sections/CustomerReportCustomerFileHealthStatus";
import {CustomerReportQueueStatus} from "./sections/CustomerReportQueueStatus";
import {CustomerReportQueuedEventsErrorStatus} from "./sections/CustomerReportQueuedEventsErrorStatus";
import {CustomerReportStatusJsonReport} from "./sections/CustomerReportStatusJsonReport";
import {
    CustomerReportNeedsVerificationFilesCountStatus
} from "./sections/CustomerReportNeedsVerificationFileCountStatus";
import {CustomerReportEtaCalculatorStatus} from "./sections/CustomerReportEtaCalculatorStatus";
import {CustomerReportErrorFilesCountStatus} from "./sections/CustomerReportErrorFileCountStatus";
import {CustomerReportMessageLogReport} from "./sections/CustomerReportMessageLogReport";
import {CustomerReportLogeniosFileHealthStatus} from "./sections/CustomerReportLogeniosFileHealthStatus";
import {SchedulerReportTaskStatus} from "./sections/CustomerReportSchedulerReportTaskStatus";
import {CustomerReportFilterSettings} from "./CustomerReportFilter";
import {CustomerReportTextsForPMsModal} from "./CustomerReportTextsForPMsModal";

export interface CustomerReportPageProps {

}

export function CustomerReportPage(props: CustomerReportPageProps) {
    const context = useContext(ConfigContext);
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState<number[]>([]);
    const location = useLocation();

    const [showTextsForPMsModal, setShowTextsForPMsModal] = useState(false);

    const reFetchReportData = async (): Promise<void> => {
        console.log("reFetchReportData function called"); // Log when the function is called

        setIsLoading(true);
        console.log("Set loading to true"); // Log when loading state is set to true

        try {
            // Use regular customer report endpoint instead of /updated since backend optimization
            // already updates the cached data when ignore/ack status changes
            await fetchCustomerReport(context, false);
            console.log("fetchCustomerReport successful",); // Log after fetching the report
        } catch (error) {
            console.error("Error fetching customer report:", error); // Log any error that occurs during fetch
        }

        setIsLoading(false);
        console.log("Set loading to false"); // Log when loading state is set to false
    };

    useEffect(() => {
        const indices = getUrlReport();
        console.log("[CustomerReportPage] report from url", indices)
        setActiveIndex(indices);
    }, [location.search]);

    useEffect(() => {
        if (customerReport.value == null) {
            fetchCustomerReport(context).then(() => {
                setIsLoading(false);
            })
        } else {
            setIsLoading(false);
        }

    }, [customerReport.value]);

    const filterNonAcknowledgedCompanies = (reportCompanies: any[]) => {
        return reportCompanies?.filter((company: any) => company?.acknowledgementStatus?.enabled !== true);
    };

    // URL handling
    function setUrlReport(reportIndex: string | number | number[]) {
        const toArray = Array.isArray(reportIndex) ? reportIndex as number[] : [Number(reportIndex)];
        const clean = toArray.filter(n => Number.isFinite(n));
        const param = clean.length > 0 ? `,${clean.join(',')}` : '';
        history.push({
            pathname: history.location.pathname,
            search: `?tab=2&report=${param}`
        });
    }

    function getUrlReport(): number[] {
        const url = new URL(window.location.href);
        const all = url.searchParams.getAll("report");
        const combined = all.join(',');
        const raw = combined.split(',');
        const nums = raw
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(s => Number(s))
            .filter(n => Number.isFinite(n));
        return nums;
    }

    function getColorForEtaReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            return "red"
        } else {
            return "green"
        }
    }

    function getColorForMessageLogReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            return "red"
        } else {
            return "green"
        }
    }

    function getColorForSchedulerReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            return "red"
        } else {
            return "green"
        }
    }

    function getColorForStatusJsonReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            if (amount > 5) {
                return "red"
            } else {
                return "orange"
            }
        } else {
            return "green"
        }

    }

    function getColorForApiGatewayReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            if (amount > 1) {
                return "red"
            } else {
                return "orange"
            }
        } else {
            return "green"
        }

    }

    function getColorForQueueStatusReportSummary(amount: number | undefined) {
        if (amount === undefined) {
            return "grey"
        }
        if (amount > 0) {
            return "red"
        } else {
            return "green"
        }
    }

    function drawStatusJsonReportSummary() {
        const report = customerReport.value?.statusJsonReport;
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report.companies);
        const ignoredCompaniesCount = report.companies.length - nonAcknowledgedCompanies.length;

        return (
            <>
                <Label
                    color={getColorForStatusJsonReportSummary(nonAcknowledgedCompanies.filter(c => !c.healthy).length)}>
                    Unhealthy companies: {nonAcknowledgedCompanies.filter(c => !c.healthy).length ?? "n/a"}
                </Label>

                <Label>Total companies checked: {report.companies.length}</Label>

                <Label>Ignored by dev: {report.companies.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.companies.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForStatusJsonReportSummary(nonAcknowledgedCompanies.filter(c => c.statusJsonExists === false).length)}>
                    Companies missing
                    statusJsonFile: {nonAcknowledgedCompanies?.filter(c => c.statusJsonExists === false).length ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawNeedVerificationFileCountReportSummary() {
        let report = customerReport.value?.needsVerificationFilesCount;
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report.companies);
        const ignoredCompaniesCount = report.companies.length - nonAcknowledgedCompanies.length;

        const healthStatus = report?.healthy;
        const companiesWithFilesOlderThanFiveMinutes = nonAcknowledgedCompanies.filter(company => company.filesOlderThanFiveMinutes > 0).length;

        return (
            <>
                <Label color={getColorForStatusJsonReportSummary(companiesWithFilesOlderThanFiveMinutes)}>
                    Healthy: {healthStatus ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report?.companies.length}</Label>

                <Label>Ignored by dev: {report.companies.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.companies.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForStatusJsonReportSummary(companiesWithFilesOlderThanFiveMinutes)}>
                    Companies with files older than five minutes
                    (non-ignored): {companiesWithFilesOlderThanFiveMinutes ?? "n/a"}
                </Label>
            </>
        );
    }


    function drawErrorFileCountReportSummary() {
        let report = customerReport.value?.errorFilesCount;
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report.companies);
        const ignoredCompaniesCount = report.companies.length - nonAcknowledgedCompanies.length;

        const healthStatus = report?.healthy?.toString();
        const filesOlderThanFiveMinutes = nonAcknowledgedCompanies.filter(company => company.filesOlderThanFiveMinutes > 0).length;

        return (
            <>
                <Label color={getColorForStatusJsonReportSummary(filesOlderThanFiveMinutes)}>
                    Healthy: {healthStatus?.charAt(0).toUpperCase() + healthStatus?.slice(1)}
                </Label>

                <Label>Total companies checked: {report?.companies.length}</Label>

                <Label>Ignored by dev: {report.companies.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.companies.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForStatusJsonReportSummary(filesOlderThanFiveMinutes)}>
                    Files older than five minutes (non-ignored): {filesOlderThanFiveMinutes ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawEtaReportSummary() {
        let report = customerReport.value?.etaCalculatorHealth;
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report.workerThreads);
        const ignoredCompaniesCount = report.workerThreads.length - nonAcknowledgedCompanies.length;

        const dbQueueSize = report.databaseQueueSize
        const operatorHealthy = report.operatorHealth?.isHealthy
        const workersHealthy = report.workerThreadsHealthy
        const orsHealthy = report.orsHealthy
        const unHealthCompanies = nonAcknowledgedCompanies.filter(company => company.isHealthy != true).length;

        return (
            <>
                <Label color='blue'>
                    DatabaseQuerySize: {dbQueueSize}
                </Label>

                <Label color={operatorHealthy ? "green" : "red"}>
                    Operator healthy: {operatorHealthy ? "True" : "False"}
                </Label>

                <Label color={workersHealthy ? "green" : "red"}>
                    Workers healthy: {workersHealthy ? "True" : "False"}
                </Label>

                <Label color={orsHealthy ? "green" : "red"}>
                    Ors healthy: {orsHealthy ? "True" : "False"}
                </Label>

                <Label color={getColorForEtaReportSummary(unHealthCompanies)}>
                    Healthy: {(unHealthCompanies === 0) ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report.workerThreads.length}</Label>

                <Label>Ignored by dev: {report.workerThreads.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by
                    pm: {report.workerThreads.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForEtaReportSummary(unHealthCompanies)}>
                    Unhealthy companies (non-ignored): {unHealthCompanies ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawMessageLogReportSummary() {
        let report = customerReport.value?.messageLogHealth;
        if (!report || report?.companies?.length == 0) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report?.companies);
        const ignoredCompaniesCount = ((report?.companies?.length ?? 0) - (nonAcknowledgedCompanies?.length ?? 0));

        const totalErrors = report.totalSendErrors ?? 0
        const unHealthCompanies = nonAcknowledgedCompanies?.length ?? 0;

        return (
            <>
                <Label color={getColorForMessageLogReportSummary(unHealthCompanies)}>
                    Healthy: {(unHealthCompanies === 0) ? "True" : "False"}
                </Label>

                <Label>Companies with errors (non-acknowledged): {nonAcknowledgedCompanies?.length ?? "0"}</Label>

                <Label>Ignored by dev: {report?.companies?.filter(c => c?.ignoreStatus?.enabled).length ?? 0}</Label>
                <Label>Ignored by
                    pm: {report?.companies?.filter(c => c?.acknowledgementStatus?.enabled).length ?? 0}</Label>

                <Label
                    color={getColorForMessageLogReportSummary(unHealthCompanies)}>
                    Total Errors: {totalErrors ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawSchedulerReportSummary() {
        let report: any = customerReport.value?.schedulerReportTaskStatus
        const isHealthy = report?.healthy ?? false;
        if (!report || report?.companies?.length == 0) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report?.companies);
        const ignoredCompaniesCount = ((report?.companies?.length ?? 0) - (nonAcknowledgedCompanies?.length ?? 0));


        return (
            <>
                <Label color={getColorForSchedulerReportSummary(isHealthy ? 0 : 1)}>
                    Healthy: {isHealthy ? "True" : "False"}
                </Label>

                <Label>Companies with unHealthyTasks (including
                    ignored): {nonAcknowledgedCompanies?.filter(company => company.unhealthyTasks > 0)?.length ?? "0"}</Label>

                <Label>Ignored companies: {ignoredCompaniesCount}</Label>

                <Label
                    color={getColorForSchedulerReportSummary(isHealthy ? 0 : 1)}>
                    Total unHealthy
                    Tasks: {nonAcknowledgedCompanies?.map(company => company.unhealthyTasks).reduce((sum, tasks) => sum + tasks, 0) ?? 0}
                </Label>
            </>
        );
    }


    function drawSftpStatusReportSummary() {
        let report = customerReport.value?.fileSystemWorkerStatus?.sftpSyncResults ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const icingaNotSuccess = nonAcknowledgedCompanies.filter(company => company.icingaState !== 'SUCCESS').length;
        const statusNotSuccess = nonAcknowledgedCompanies.filter(company => company.status !== 'SUCCESS').length;

        return (
            <>
                <Label color={getColorForStatusJsonReportSummary(icingaNotSuccess + statusNotSuccess)}>
                    Healthy: {(icingaNotSuccess === 0 && statusNotSuccess === 0) ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report.length}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label color={getColorForStatusJsonReportSummary(icingaNotSuccess)}>
                    Failed Icinga instances: {icingaNotSuccess ?? "n/a"}
                </Label>

                <Label color={getColorForStatusJsonReportSummary(statusNotSuccess)}>
                    Failed Status instances: {statusNotSuccess ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawApiGatewayStatusReportSummary() {
        let report = customerReport.value?.apiGatewayStatus?.companyApiStatus ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const statusNotSuccess = nonAcknowledgedCompanies.filter(company => company.status !== 'OK').length;

        return (
            <>
                <Label color={getColorForApiGatewayReportSummary(statusNotSuccess)}>
                    Healthy: {(statusNotSuccess === 0) ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report.length}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label color={getColorForApiGatewayReportSummary(statusNotSuccess)}>
                    Failed Status instances: {statusNotSuccess ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawQueueStatusReportSummary() {
        let report = customerReport.value?.queueStatus?.companies ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const statusNotSuccess = nonAcknowledgedCompanies.filter(company => company.unprocessedEventsOlderThanFiveMinutes > 0).length;

        return (
            <>
                <Label color={getColorForQueueStatusReportSummary(statusNotSuccess)}>
                    Healthy: {(statusNotSuccess === 0) ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report.length}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label color={getColorForQueueStatusReportSummary(statusNotSuccess)}>
                    Failed Status instances: {statusNotSuccess ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawQueuedEventsErrorStatusReportSummary() {
        let report = customerReport.value?.queuedEventsErrorStatus?.companies ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const statusNotSuccess = nonAcknowledgedCompanies.filter(company => company.processedEventsWithErrorCount > 0).length;

        return (
            <>
                <Label color={getColorForQueueStatusReportSummary(statusNotSuccess)}>
                    Healthy: {(statusNotSuccess === 0) ? "True" : "False"}
                </Label>

                <Label>Total companies checked: {report.length}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label color={getColorForQueueStatusReportSummary(statusNotSuccess)}>
                    Failed Status instances: {statusNotSuccess ?? "n/a"}
                </Label>
            </>
        );
    }


    function drawCustomerFileStatusReportSummary() {
        let report = customerReport.value?.fileSystemWorkerStatus?.customerOldFiles ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const totalCompanies = report.length;
        const oldFileAmount = nonAcknowledgedCompanies.filter(company => company?.oldFileAmount > 0).length;
        const healthyStatus = nonAcknowledgedCompanies.filter(company => company?.healthy !== true).length;

        return (
            <>
                <Label
                    color={getColorForStatusJsonReportSummary(healthyStatus)}>
                    Healthy: {(healthyStatus > 0) ? "False" : "True"}
                </Label>

                <Label>Total companies checked: {totalCompanies}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForStatusJsonReportSummary(oldFileAmount)}>
                    Old File Amount: {oldFileAmount ?? "n/a"}
                </Label>
            </>
        );
    }

    function drawLogeniosFileStatusReportSummary() {
        let report = customerReport.value?.fileSystemWorkerStatus?.logeniosOldFiles ?? [];
        if (!report) return null;

        const nonAcknowledgedCompanies = filterNonAcknowledgedCompanies(report);
        const ignoredCompaniesCount = report.length - nonAcknowledgedCompanies.length;

        const totalCompanies = report.length;
        const oldFileAmount = nonAcknowledgedCompanies.filter(company => company?.oldFileAmount > 0).length;
        const healthyStatus = nonAcknowledgedCompanies.filter(company => company?.healthy !== true).length;

        return (
            <>
                <Label
                    color={getColorForStatusJsonReportSummary(healthyStatus)}>
                    Healthy: {(healthyStatus > 0) ? "False" : "True"}
                </Label>

                <Label>Total companies checked: {totalCompanies}</Label>

                <Label>Ignored by dev: {report.filter(c => c?.ignoreStatus?.enabled).length}</Label>
                <Label>Ignored by pm: {report.filter(c => c?.acknowledgementStatus?.enabled).length}</Label>

                <Label
                    color={getColorForStatusJsonReportSummary(oldFileAmount)}>
                    Old File Amount: {oldFileAmount ?? "n/a"}
                </Label>
            </>
        );
    }


    function deepCopy(obj: any): any {
        return JSON.parse(JSON.stringify(obj));
    }

    function handleAccordionClick(e: any, titleProps: { index: any; }) {
        console.log("handleAccordionClick", titleProps)
        const {index} = titleProps
        let newIndex = deepCopy(activeIndex)
        if (newIndex.includes(index)) {
            newIndex = activeIndex.filter(i => i != index)
        } else {
            newIndex.push(index)
        }

        setUrlReport(newIndex)
        setActiveIndex(newIndex)
    }

    function addAccordionSection(title: string, index: number, content: any) {
        let shortReport: JSX.Element;
        let data = null;
        switch (index) {
            // todo add these summaries
            case 0:
                shortReport = drawApiGatewayStatusReportSummary()
                data = customerReport.value?.apiGatewayStatus?.companyApiStatus;
                break;
            case 1:
                shortReport = drawSftpStatusReportSummary()
                data = customerReport.value?.fileSystemWorkerStatus?.sftpSyncResults;
                break;
            case 2:
                shortReport = drawCustomerFileStatusReportSummary()
                data = customerReport.value?.fileSystemWorkerStatus?.customerOldFiles;
                break;
            case 3:
                shortReport = drawLogeniosFileStatusReportSummary()
                data = customerReport.value?.fileSystemWorkerStatus?.logeniosOldFiles;
                break;
            case 4:
                shortReport = drawQueueStatusReportSummary()
                data = customerReport.value?.queueStatus?.companies;
                break;
            case 5:
                shortReport = drawQueuedEventsErrorStatusReportSummary()
                data = customerReport.value?.queuedEventsErrorStatus?.companies;
                break;
            case 6:
                shortReport = drawStatusJsonReportSummary()
                data = customerReport.value?.statusJsonReport;
                break;
            case 7:
                shortReport = drawNeedVerificationFileCountReportSummary()
                data = customerReport.value?.needsVerificationFilesCount;
                break;
            case 8:
                shortReport = drawErrorFileCountReportSummary()
                data = customerReport.value?.errorFilesCount;
                break;
            case 9:
                shortReport = drawEtaReportSummary()
                data = customerReport.value?.etaCalculatorHealth;
                break;
            case 10:
                shortReport = drawMessageLogReportSummary()
                data = customerReport.value?.messageLogHealth;
                break;
            case 11:
                shortReport = drawSchedulerReportSummary()
                data = customerReport.value?.schedulerReportTaskStatus;
                break;
        }

        let notData = data == null || data.length === 0;

        return (
            <>
                <Accordion.Title
                    active={activeIndex.includes(index)}
                    index={index}
                    onClick={handleAccordionClick}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: notData ? '#ea3d3d' : 'transparent',
                    }} // Add flex layout to space items
                >
                <span>
                    <Icon name='dropdown'/>
                    {title}{notData && <span
                    style={{
                        marginLeft: 100,
                        fontSize: 24,
                        fontWeight: "bold",
                        color: "black"
                    }}>
                        NO DATA
                    </span>
                }
                </span>



                    <>
                    {shortReport && (
                        <div style={{marginLeft: 'auto'}}> {/* Add margin to the left to push content to the right */}
                            {shortReport}
                        </div>
                    )}
                    </>
                </Accordion.Title>
                <Accordion.Content active={activeIndex.includes(index)}>
                    {content}
                </Accordion.Content>
            </>
        );
    }

    if (isLoading) {
        return <div>Loading customer report...</div>
    } else {
        console.log("CustomerReportPage", customerReport.value)
        return <div id={"CustomerReportPage"} style={{paddingLeft: "5px"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
                <CustomerReportFilterSettings/>
                <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%"}}>
                    <Button
                        onClick={() => {
                            setShowTextsForPMsModal(true)
                        }}
                        disabled={isLoading}
                        primary
                        style={{alignSelf: "flex-end"}}
                    >
                        Show Texts for PMs
                    </Button>
                </div>
            </div>

            <CustomerReportTextsForPMsModal
                isOpen={showTextsForPMsModal}
                onClose={() => setShowTextsForPMsModal(false)}
            />
            <Header content={"Dev Customer Report"} style={{marginTop: "20px"}}/>
            <Accordion id={"DevCustomerReportSection"} styled fluid>
                {addAccordionSection(
                    "ApiGateway Status",
                    0,
                    <CustomerReportApiGatewayStatus reFetchReportData={reFetchReportData}/>
                )}
                {addAccordionSection("Scheduler Report Task Status", 11, <SchedulerReportTaskStatus
                    reFetchReportData={reFetchReportData}/>)}
            </Accordion>
            <Divider/>
            <Header content={"PM Customer Report"} style={{marginTop: "20px"}}/>

            <Accordion id={"DevCustomerReportSection"} styled fluid>

                {addAccordionSection("Sftp Status", 1, <CustomerReportSftpStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Logenios2Customer File Status", 2, <CustomerReportCustomerFileHealthStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Customer2Logenios File Status", 3, <CustomerReportLogeniosFileHealthStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Queue Status", 4, <CustomerReportQueueStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Queued Events Error Status", 5, <CustomerReportQueuedEventsErrorStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Status.json Report", 6, <CustomerReportStatusJsonReport
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Needs Verification Files Count", 7,
                    <CustomerReportNeedsVerificationFilesCountStatus
                        reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Error Files Count", 8, <CustomerReportErrorFilesCountStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("Eta Calculator", 9, <CustomerReportEtaCalculatorStatus
                    reFetchReportData={reFetchReportData}/>)}
                {addAccordionSection("MessageLog Report", 10, <CustomerReportMessageLogReport
                    reFetchReportData={reFetchReportData}/>)}

            </Accordion>
        </div>
    }


}